import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzHtml,
    EzUrl
} from '/ezlibrary/helpers/EzHelpers.js';
import {
    EzRegistrationState,
    EzElementEventName,
    EzClockerCookieName
} from '/ezlibrary/enums/EzEnumerations.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzLocalBrowserInfo } from '/ezlibrary/analytics_metrics/EzLocalBrowserInfo.js';
import { EzCookies } from '/public/javascript/common/ez-cookies.js';
import { EzUrlHelper } from '/public/javascript/common/ezclocker-url-helper2.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';
import { EzLayoutHelper } from '/public/javascript/common/ezclocker-layout-helper.js';
import { EzBrowserInfo } from '/public/javascript/common/ezclocker-mobile-helper.js';
import { EzAnimator } from '/ezlibrary/ez-animator.js';
import { EzValidation } from '/public/javascript/common/ez-validation.js';
import { EzAnalyticsAndMetrics } from '/ezlibrary/analytics_metrics/EzAnalyticsAndMetrics.js';
import { EzValidationResponseStatus } from '/ezlibrary/EzValidationResponse.js';
import { EzRequestPasswordResetDialog } from '/public/webcomponents/EzRequestPasswordResetDialog/ez-request-password-reset-dialog.js';
import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Controller/view for ezClocker Sign-in page
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzSignInView } from '/public/signin/ez-signin.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance ready for use when the below evaluates to true:
 *     globalThis.ezApi.ezclocker[EzSignInView.ezApiName] &&
 *     globalThis.ezApi.ezclocker[EzSignInView.ezApiName].ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen to onReady event:
 *     EzClass.ezWaitReady(
 *         EzSignInView.ezEventNames.onReady,
 *         this.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton Instance Reference:
 *     Inside this class: EzSignInView.ezInstance
 *     Outside this class: globalThis.ezApi.ezclocker.ezSignInView
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzSignInView extends EzClass {
    /**
     * @private @field
     * Stores the remember me option
     *
     * @type {boolean}
     */
    #ezRememberMe = false;
    /**
     * @public @property @getter
     * Gets the remember me option
     *
     * @returns {boolean}
     */
    get ezRememberMe() {
        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId)) {
            this.#ezRememberMe = EzUx.isCheckboxChecked(EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId);
        }

        return this.#ezRememberMe;
    }
    /**
     * @public @property @setter
     * Sets the remember me option
     *
     * @param {boolean} ezRememberMe
     */
    set ezRememberMe(ezRememberMe) {
        this.#ezRememberMe = EzBoolean.booleanOrFalse(ezRememberMe);

        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId)) {
            EzUx.setCheckboxValue(
                EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId,
                this.#ezRememberMe);
        }
    }

    #ezCurrentUserName = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the current user name
     *
     * @returns {string}
     */
    get ezCurrentUserName() {
        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.userNameInputId)) {
            this.#ezCurrentUserName = EzString.stringWithTextOrEmpty(
                EzUx.getInputValue(EzSignInView.ezInstance.ezIds.inputs.userNameInputId))
                .trim();

            EzUx.setInputValue(
                EzSignInView.ezInstance.ezIds.inputs.userNameInputId,
                this.#ezCurrentUserName);
        }

        return this.#ezCurrentUserName;
    }
    /**
     * @public @property @setter
     * Sets the current user name
     *
     * @param {string} ezCurrentUserName
     * Default: EzString.EMPTY
     */
    set ezCurrentUserName(ezCurrentUserName = EzString.EMPTY) {
        this.#ezCurrentUserName = EzString.stringWithTextOrEmpty(ezCurrentUserName).trim();

        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.userNameInputId)) {
            EzUx.setInputValue(
                EzSignInView.ezInstance.ezIds.inputs.userNameInputId,
                this.#ezCurrentUserName);
        }
    }

    #ezCurrentPassword = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the current user name
     *
     * @returns {string}
     */
    get ezCurrentPassword() {
        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.passwordInputId)) {
            this.#ezCurrentPassword = EzString.stringWithTextOrEmpty(
                EzUx.getInputValue(EzSignInView.ezInstance.ezIds.inputs.passwordInputId));
        }

        return this.#ezCurrentPassword;
    }
    /**
     * @public @property @setter
     * Sets the current user name
     *
     * @param {string} error
     */
    set ezCurrentPassword(ezCurrentPassword) {
        this.#ezCurrentPassword = EzString.stringWithTextOrEmpty(ezCurrentPassword);

        if (EzUx.elementExists(EzSignInView.ezInstance.ezIds.inputs.passwordInputId)) {
            EzUx.setInputValue(
                EzSignInView.ezInstance.ezIds.inputs.passwordInputId,
                this.#ezCurrentPassword);
        }
    }

    /**
     * @private @field
     * Stores the error message to display
     *
     * @type {string}
     */
    #signInError = null;
    /**
     * @public @property @getter
     * Gets the error message to display
     *
     * @returns {string}
     */
    get signInError() {
        return this.#signInError;
    }
    /**
     * @public @property @setter
     * Sets the error message to display
     *
     * @param {string} signInError
     * Default: EzString.EMPTY
     */
    set error(signInError = EzString.EMPTY) {
        this.#signInError = EzString.stringWithTextOrEmpty(signInError);
    }

    /**
     * @public @readonly @property
     * Gets a map of id => element id
     * @returns {object}
     */
    get ezIds() {
        return {
            pageBodyId: 'EzPageBody',
            pageHeaderId: 'EzPageHeader',
            pageContentId: 'EzPageContent',
            forms: {
                ezSignInFormId: 'EzClockerSignInForm'
            },
            containers: {
                signInDialogLayoutContainerId: 'EzSignInDialogLayoutContainerId',
                signInDialogContainerId: 'EzSignInDialogContainer',
                hideDialogsContainerId: 'EzHideDialogsDiv',
                signInContainerId: 'EzSignInContainer',
                signInBoxId: '_SignInBox',
                signInErrorTopId: 'EzSignInErrorTop',
                welcomeBackSubBannerId: 'EzWelcomeBackSubBanner',
                signInButtonContainerId: 'EzSignInButtonContainer',
                forgotPasswordContainerId: 'EzForgotPasswordContainer',
                signUpContainerId: 'EzSignUpContainer',
                signInPasswordInputContainerId: 'EzSignInPasswordInputContainer',
                signInPasswordInputLayoutContainerId: 'EzSignInPasswordInputLayoutContainer',
                toggleShowHidePasswordContainerId: 'EzToggleShowHidePasswordContainer'
            },
            links: {
                pageHeaderLogoLinkId: 'EzPageHeaderLogoLink',
                forgotPasswordLinkId: 'EzForgotPasswordLink',
                signUpLinkId: 'EzSignUpLink'
            },
            buttons: {
                signInButtonId: 'EzSignInButton',
                cancelButtonId: 'EzCancelSignInButton',
                passwordHideButtonId: 'EzPasswordHideButton',
                passwordShowButtonId: 'EzPasswordShowButton'
            },
            images: {
                pageHeaderLogoId: 'EzPageHeaderLogo',
                showPasswordImg: 'EzShowPasswordImg',
                hidePasswordImg: 'EzHidePasswordImg'
            },
            inputs: {
                rememberEmailCheckboxId: 'EzRememberEmail',
                rememberMeCheckboxId: 'remember-me',
                userNameInputId: 'j_username',
                passwordInputId: 'j_password'
            }
        };
    }

    /**
     * @public @readonly @property
     * Gets the mobile sign-in page url
     *
     * @returns {string}
     */
    get mobilePagePath() {
        return globalThis.ezApi.ezclocker.ezNavigation.ezRemoveVersionFromUri(
            `p/download${globalThis.ezApi.ezclocker.ezUrlHelper.ezGetWindowLocationSearch()}`);
    }

    /**
     * @public @readonly @property
     * Gets EzSigninView's page header html
     *
     * @returns {string}
     */
    get ezSignInViewPageHeaderHTML() {
        return EzHtml.build`
            <div
                id="${EzSignInView.ezInstance.ezIds.pageHeaderId}"
                class="ezPageHeader-ezClockerWhite ez-bottom-shadow">
                <!-- style="position:fixed;top:0;left:0;width:100%;z-index:20;"> -->
                <a
                    id="${EzSignInView.ezInstance.ezIds.links.pageHeaderLogoLinkId}"
                    href="#"
                    onclick="globalThis.ezApi.ezclocker.ezNavigation.ezNavigateToMain();">
                    <img
                        id="${EzSignInView.ezInstance.ezIds.images.pageHeaderLogoId}"
                        class="ezPageHeaderLogo"
                        src="/public/images/logos/ezclocker_logo_2015.svg"
                        alt="ezClocker"/>
                </a>
            </div>`;
    }

    /**
     * @public @readonly @property
     * Builds the HTML sign-in form
     *
     * @returns {string}
     */
    get ezSigninDialogHtml() {
        return EzHtml.build`
            <div
                id="${EzSignInView.ezInstance.ezIds.containers.signInDialogLayoutContainerId}"
                class="ez-sign-in-dialog-layout-container">
                <div
                    id="${EzSignInView.ezInstance.ezIds.containers.signInDialogContainer}_left">
                    &nbsp;
                </div>
                <div
                    id="${EzSignInView.ezInstance.ezIds.containers.signInDialogContainer}"
                    class="ez-sign-in-dialog-container">
                    <form
                        id="${EzSignInView.ezInstance.ezIds.forms.ezSignInFormId}"
                        action="${globalThis.ezApi.ezclocker.ezNavigation.ezGetSigninUrl()}"
                        method="POST">
                        <div
                            id="${EzSignInView.ezInstance.ezIds.containers.signInContainerId}">
                            <div
                                class="ez-sign-in-dialog-banner ezGrid-align-center">
                                Sign In
                                <div
                                    id="${EzSignInView.ezInstance.ezIds.containers.welcomeBackSubBannerId}"
                                    class="ez-sign-in-dialog-subbanner"
                                    style="display:none">
                                    Welcome back!
                                </div>
                            </div>
                            <div
                                id="EzSignInErrorTop"
                                class="ezErrorBox"
                                style="display:none">
                            </div>
                            <input
                                id="EzMobile"
                                type="hidden"
                                name="mobile"
                                value="1"/>
                            <div
                                class="ezContainers-large-input-box">
                                <label
                                    class="ezMajorInputLabel"
                                    for="${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}"">
                                    Email Address or Phone Number
                                </label>
                                <input
                                    id="${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}"
                                    class="ezLargeInputEditor ezFullWidthEditor"
                                    type="text"
                                    name="${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}"/>
                            </div>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}_ErrorContainer"
                                class="ezErrorBox"
                                style="display:none">
                            </div>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.containers.ezSignInPasswordInputContainerId}"
                                class="ezContainers-large-input-box">
                                <label
                                    class="ezMajorInputLabel"
                                    for="${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}">
                                    Password
                                </label>
                                <div
                                    id="${EzSignInView.ezInstance.ezIds.containers.signInPasswordInputLayoutContainerId}"
                                    class="ezAutoCol_AxMin ezGrid-align-full ezGrid-vertical-align-middle ezGrid-col-gap_8">
                                    <input
                                        id="${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}"
                                        class="ezLargeInputEditor ezFullWidthEditor"
                                        name="${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}"
                                        type="password"
                                        autocomplete="new-password"/>
                                    <div
                                        id="${EzSignInView.ezInstance.ezIds.containers.toggleShowHidePasswordContainerId}"
                                        class="ezGrid-align-middle-center">
                                        <button
                                            id="${EzSignInView.ezInstance.ezIds.buttons.passwordShowButtonId}"
                                            class="ezButtons-tool-navy-large"
                                            type="button"
                                            title="Show Password">
                                            <img
                                                id="${EzSignInView.ezInstance.ezIds.images.showPasswordImg}"
                                                class="ez-sign-in-password-show-hide-img"
                                                src="/public/images/icons/view-white.svg"/>
                                            <img
                                                id="${EzSignInView.ezInstance.ezIds.images.hidePasswordImg}"
                                                class="ez-sign-in-password-show-hide-img"
                                                src="/public/images/icons/hide-view-white.svg"
                                                style="display:none"/>
                                        </button>
                                        <!--
                                        <button
                                            id="${EzSignInView.ezInstance.ezIds.buttons.passwordHideButtonId}"
                                            class="ezButtons-tool-navy"
                                            style="display:none"
                                            type="button"
                                            title="Hide Password">
                                            <img
                                                id="${EzSignInView.ezInstance.ezIds.images.hidePasswordImg}"
                                                class="ez-sign-in-password-show-hide-img"
                                                src="/public/images/icons/hide-view-white.svg"/>
                                        </button>
                                        -->
                                    </div>
                                </div>
                                <div
                                    id="${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}_ErrorContainer"
                                    class="ezErrorBox"
                                    style="display:none">
                                </div>
                            </div>
                            <div
                                id="EzRememberMeCheckboxInputContainer"
                                class="ezContainers-large-input-box">
                                <div>
                                    <input
                                        id="${EzSignInView.ezInstance.ezIds.inputs.rememberMeCheckboxId}"
                                        type="checkbox"
                                        name="${EzSignInView.ezInstance.ezIds.inputs.rememberMeCheckboxId}"
                                        checked style="display:none"/>
                                    <input
                                        id="${EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId}"
                                        type="checkbox"
                                        name="${EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId}"/>
                                    <label
                                        for="${EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId}">
                                        Remember my user name or email address for seven days.
                                    </label>
                                </div>
                            </div>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId}_ErrorContainer"
                                style="display:none">
                            </div>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.containers.signInButtonContainerId}"
                                class="ezAutoCol_A">
                                <div
                                    class="ezRightAlignedButtonContainer">
                                    <button
                                        id="${EzSignInView.ezInstance.ezIds.buttons.signInButtonId}"
                                        class="ezButtons-action-ezActionGreen ezMiddleAlign"
                                        type="button">
                                        Sign In
                                    </button>
                                    <button
                                        id="${EzSignInView.ezInstance.ezIds.buttons.cancelButtonId}"
                                        class="ezButtons-action-ezClockerYellow ezMiddleAlign"
                                        type="button">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            <hr/>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.containers.forgotPasswordContainerId}"
                                class="ezCenterAlignedContainer">
                                <a
                                    id="${EzSignInView.ezInstance.ezIds.links.forgotPasswordLinkId}"
                                    href="#"
                                    class="ezLargeNavyLink">
                                    Forgot your password?
                                </a>
                            </div>
                            <div
                                id="${EzSignInView.ezInstance.ezIds.containers.signUpContainerId}"
                                class="ezCenterAlignedContainer">
                                Don't have an account?
                                <a
                                    id="EzSignUpLink"
                                    href="#"
                                    class="ezLargeNavyLink">
                                    Sign Up for free!
                                </a>
                            </div>
                        </div>
                    </form>
                </div>
                <div
                    id="${EzSignInView.ezInstance.ezIds.containers.signInDialogContainer}_right">
                    &nbsp;
                </div>
            </div>`;
    }

    /**
     * @public @method
     * Initializes EzSignInView
     *
     * @returns {EzSignInView}
     */
    ezInit() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzSignInView.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordSubmitted,
            EzSignInView.ezInstance.ezShow);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzSignInView.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordCanceled,
            EzSignInView.ezInstance.ezShow);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzSignInView.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordError,
            EzSignInView.ezInstance.ezShow);

        EzSignInView.ezInstance.ezRememberMe = false;

        EzSignInView.ezInstance.ezCurrentUserName = EzString.EMPTY;

        EzSignInView.ezInstance.ezError = EzString.EMPTY;

        EzSignInView.ezInstance.ezInitUX();

        return EzSignInView.ezInstance;
    }

    /**
     * @public @method
     * Resets the dialog to its default state
     */
    ezResetDialog() {
        EzUx.hide(EzSignInView.ezInstance.ezIds.containers.welcomeBackSubBannerId);

        EzUx.setInputValue(
            EzSignInView.ezInstance.ezIds.inputs.passwordInputId,
            EzString.EMPTY);

        EzUx.setInputValue(
            EzSignInView.ezInstance.ezIds.inputs.userNameInputId,
            EzString.EMPTY);

        EzUx.setCheckboxValue(
            EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId,
            false);

        EzUx.setCheckboxValue(
            EzSignInView.ezInstance.ezIds.inputs.rememberEmailCheckboxId,
            false);
    }

    /**
     * @public @method
     * Initializes the view's UX
     */
    ezInitUX() {
        if (globalThis.ezApi.ezclocker.ezBrowserInfo.ezRedirectIfMobilePhone(EzSignInView.ezInstance.mobilePagePath)) {
            return;
        }

        EzUx.appendContent(
            EzSignInView.ezInstance.ezIds.pageBodyId,
            EzHtml.build`
                <div
                    id="${EzSignInView.ezInstance.ezIds.pageContentId}">
                    ${EzSignInView.ezInstance.ezSignInViewPageHeaderHTML}
                    ${EzSignInView.ezInstance.ezSigninDialogHtml}
                </div>`);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.inputs.userNameInputId,
            EzElementEventName.BLUR,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezHandleUsernameInputBlurEvent);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.inputs.passwordInputId,
            EzElementEventName.BLUR,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezHandlePasswordInputBlurEvent);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.buttons.passwordShowButtonId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezToggleShowHidePassword);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.buttons.signInButtonId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezSubmit);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.buttons.cancelButtonId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezCancel);

        globalThis.document.onkeydown = EzSignInView.ezInstance.ezHandleEnterKey;

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.links.forgotPasswordLinkId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            EzSignInView.ezInstance.ezShowRequestPasswordReset);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.links.signUpLinkId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            () => globalThis.ezApi.ezclocker.ezNavigation.navigateToPublicPage('signup.html'));

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzSignInView.ezInstance.ezIds.links.signUpLinkId,
            EzElementEventName.CLICK,
            EzSignInView.ezApiName,
            () => globalThis.ezApi.ezclocker.ezNavigation.ezNavigateToSignUp(EzSignInView.ezInstance.#ezCurrentUserName));

        /*
        globalThis.ezApi.ezclocker.ezLayoutHelper.sizeContentHeightWithinContainer('window', 'EzPage');

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            'window',
            EzElementEventName.RESIZE,
            EzSignInView.ezApiName,
            () => globalThis.ezApi.ezclocker.ezLayoutHelper.sizeContentHeightWithinContainer('window', 'EzPage'));
        */

        EzSignInView.ezInstance.ezShow();

        return this;
    }

    /**
     * @public @method
     * Shows the sign-in view
     */
    ezShow() {
        EzSignInView.ezInstance.ezResetDialog();

        EzUx.show(EzSignInView.ezInstance.ezIds.containers.signInContainerId);

        EzSignInView.ezInstance.ezLoadCookies();

        EzSignInView.ezInstance.ezSigninError = globalThis.ezApi.ezclocker.ezUrlHelper.getErrorParam();

        const emailParam = globalThis.ezApi.ezclocker.ezUrlHelper.getEmailParam();

        const usernameParam = globalThis.ezApi.ezclocker.ezUrlHelper.getUserNameParam();

        if (EzString.hasText(emailParam)) {
            EzSignInView.ezInstance.ezCurrentUserName = emailParam;
        } else if (EzString.hasText(usernameParam)) {
            EzSignInView.ezInstance.ezCurrentUserName = usernameParam;
        }

        const focusInputId = !EzString.hasText(EzSignInView.ezInstance.ezCurrentUserName)
            ? EzSignInView.ezInstance.ezIds.inputs.userNameInputId
            : EzSignInView.ezInstance.ezIds.inputs.passwordInputId;

        EzUx.focus(focusInputId);

        if (EzString.hasText(EzSignInView.ezInstance.ezSigninError)) {
            const decodedError = EzUrl.decodeURL(EzSignInView.ezInstance.ezSigninError).replaceAll('&amp;', '&');
            globalThis.ezApi.ezclocker.ezLogger.info(`Sign-in error: ${decodedError}`);
            EzSignInView.ezInstance.ezShowSignInErrorBox('EzSignInErrorTop', decodedError);
        }
    }

    /**
     * @public @method
     * Hides the dialog without submitting data
     */
    ezClose() {
        EzSignInView.ezInstance.ezResetDialog();
    }

    /**
     * @public @method
     */
    ezCancel() {
        EzSignInView.ezInstance.ezClose();

        globalThis.ezApi.ezclocker.ezNavigation.ezSilentSignOut()
            .then(
                globalThis.ezApi.ezclocker.ezNavigation.ezNavigateToMain,
                globalThis.ezApi.ezclocker.ezNavigation.ezNavigateToMain);
    }

    /**
     * @public @method
     * Handles the sign-in submit button click
     */
    ezSubmit() {
        if (EzBoolean.isFalse(EzSignInView.ezInstance.ezValidateSignInFields())) {
            // Not valid, do not submit
            return false;
        }

        EzSignInView.ezInstance.ezSaveCookies();

        EzUx.submitForm(EzSignInView.ezInstance.ezIds.forms.ezSignInFormId);

        return true;
    }

    /**
     * @protected @method
     * Handles the checkbox change for remember me
     *
     * @param {object} ezEvent
     */
    ezHandleRememberMeChange(ezEvent) {
        EzSignInView.ezInstance.ezRememberMe = EzObject.isValid(ezEvent) && EzObject.isValid(ezEvent.data.event.currentTarget)
            ? ezEvent.data.event.currentTarget.checked
            : false;
    }

    /**
     * @public @method
     * Saves the user name + remember me settings to the cookies.
     */
    ezSaveCookies() {
        if (EzBoolean.isTrue(EzSignInView.ezInstance.ezRememberMe)) {
            globalThis.ezApi.ezclocker.ezCookies.ezSetPathExpiryCookie(
                EzClockerCookieName.REMEMBER_ME,
                true);

            globalThis.ezApi.ezclocker.ezCookies.ezSetPathExpiryCookie(
                EzClockerCookieName.USER_NAME,
                EzSignInView.ezInstance.ezCurrentUserName);
        } else {
            globalThis.ezApi.ezclocker.ezCookies.ezSetPathExpiryCookie(
                EzClockerCookieName.REMEMBER_ME,
                false);

            globalThis.ezApi.ezclocker.ezCookies.ezSetPathExpiryCookie(
                EzClockerCookieName.USER_NAME,
                EzString.EMPTY);
        }
    }

    /**
     * @public @method
     * Loads dialog values from cookies (if any)
     */
    ezLoadCookies() {
        EzSignInView.ezInstance.ezRememberMe = EzBoolean.isTrue(
            globalThis.ezApi.ezclocker.ezCookies.ezReadCookie(EzClockerCookieName.REMEMBER_ME));

        const emailCookie = globalThis.ezApi.ezclocker.ezCookies.ezReadCookie(EzClockerCookieName.USER_NAME);

        if (EzString.hasText(emailCookie) && !EzString.hasText(EzSignInView.ezInstance.ezCurrentUserName)) {
            EzSignInView.ezInstance.ezCurrentUserName = emailCookie;

            EzUx.show(EzSignInView.ezInstance.ezIds.containers.welcomeBackSubBannerId);
        }
    }

    /**
     * @public @method
     * Shows the password reset dialog
     */
    ezShowRequestPasswordReset() {
        globalThis.ezApi.ezclocker.ezRequestPasswordResetDialog.ezShow(EzSignInView.ezInstance.ezCurrentUserName);
    }

    /**
     * @public @method
     * Validates the sign-in fields.
     */
    ezValidateSignInFields() {
        EzSignInView.ezInstance.ezHideAllErrorContainersAndInputErrorHighlighting();

        let isValid = true;

        let ezValidationResponse = globalThis.ezApi.ezclocker.ezValidation.ezValidateUsername(
            EzSignInView.ezInstance.ezCurrentUserName,
            true);

        if (EzValidationResponseStatus.VALID !== ezValidationResponse.ezStatus) {
            EzSignInView.ezInstance.ezShowUsernameError('Please enter the user name or mobile phone number you used to sign-up.');

            isValid = false;
        }

        ezValidationResponse = globalThis.ezApi.ezclocker.ezValidation.ezValidateLegacyPassword(
            EzSignInView.ezInstance.ezCurrentPassword,
            true);

        if (EzValidationResponseStatus.VALID !== ezValidationResponse.ezStatus) {
            EzSignInView.ezInstance.ezShowPasswordError(`Please enter the password you created when you signed-up.`);

            isValid = false;
        }

        return isValid;
    }

    /**
     * @protected @method
     * Handles the Username input's OnBlur event
     */
    ezHandleUsernameInputBlurEvent() {
        EzSignInView.ezInstance.ezHideUsernameError();
    }

    /**
     * @public @method
     * Highlights the username input box and shows the provided error message.
     * @param {String} em
     * Default: EzString.EMPTY
     */
    ezShowUsernameError(em = EzString.EMPTY) {
        if (!EzString.hasText(em)) {
            return;
        }

        EzUx.showInputError(EzSignInView.ezInstance.ezIds.inputs.userNameInputId);

        EzSignInView.ezInstance.ezShowSignInErrorBox(
            `${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}_ErrorContainer`,
            em);

        EzUx.focus(EzSignInView.ezInstance.ezIds.inputs.userNameInputId);
    }

    /**
     * @public @method
     * Returns the username input box to it's non-error state and then hides all the error containers.
     */
    ezHideUsernameError() {
        EzUx.hideInputError(EzSignInView.ezInstance.ezIds.inputs.userNameInputId);

        EzSignInView.ezInstance.ezHideSignInErrorContainers();
    }

    /**
     * @protected @method
     * Handles the password input's OnBlur event
     */
    ezHandlePasswordInputBlurEvent(event) {
        if (event?.relatedTarget?.id === 'EzSignInErrorResetPasswordLink') {
            return;
        }
        EzSignInView.ezInstance.ezHidePasswordError();
    }

    /**
     * @public @method
     * Highlights the password input box and shows the provided error message.
     * @param {String} em
     * Default: EzString.EMPTY
     */
    ezShowPasswordError(em) {
        if (!EzString.hasText(em)) {
            return;
        }

        EzUx.showInputError(EzSignInView.ezInstance.ezIds.inputs.passwordInputId);

        EzSignInView.ezInstance.ezShowSignInErrorBox(
            `${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}_ErrorContainer`,
            em.replaceAll('+', ' '));

        EzUx.focus(EzSignInView.ezInstance.ezIds.inputs.passwordInputId);
    }

    /**
     * @public @method
     * Returns the password input box to it's non-error state and then hides all the error containers.
     */
    ezHidePasswordError() {
        EzUx.hideInputError(EzSignInView.ezInstance.ezIds.inputs.passwordInputId);

        EzSignInView.ezInstance.ezHideSignInErrorContainers();
    }

    /**
     * @public @method
     * Shows an error in the global error container at the top of the dialog
     * @param {string} em
     * Default: EzString.EMPTY
     */
    ezShowGlobalError(em = EzString.EMPTY) {
        if (!EzString.hasText(em)) {
            return;
        }

        EzSignInView.ezInstance.ezShowSignInErrorBox(
            EzSignInView.ezInstance.ezIds.containers.signInErrorTopId,
            em);
    }

    /**
     * @public @function
     * Hides all error containers
     */
    ezHideGlobalError() {
        EzSignInView.ezInstance.ezHideSignInErrorContainers();
    }

    /**
     * @public @function
     * Hides all input error highlighting and all error containers
     */
    ezHideAllErrorContainersAndInputErrorHighlighting() {
        EzUx.hideInputError(EzSignInView.ezInstance.ezIds.inputs.userNameInputId);

        EzUx.hideInputError(EzSignInView.ezInstance.ezIds.inputs.passwordInputId);

        EzSignInView.ezInstance.ezHideSignInErrorContainers();
    }


    /**
     * @public @method
     * Shows the error message box
     * @param {String} errorContainerId
     * Default: EzSignInView.ezInstance.ezIds.containers.signInErrorTopId
     * @param {String} em
     * Default: EzString.EMPTY
     */
    ezShowSignInErrorBox(errorContainerId = EzSignInView.ezInstance.ezIds.containers.signInErrorTopId, em = EzString.EMPTY) {
        if (!EzString.hasText(em)) {
            return;
        }

        if (!EzString.hasText(errorContainerId)) {
            errorContainerId = EzSignInView.ezInstance.ezIds.containers.signInErrorTopId;
        }

        em = em.replaceAll('+', ' ')
            .replaceAll(
                'RESET_YOUR_PASSWORD_LINK',
                EzHtml.build`
                    <a
                        id="EzSignInErrorResetPasswordLink"
                        href="javascript:void(0)">
                        reset your password
                    </a>`);

        const currentEm = EzUx.getContent(errorContainerId);

        EzUx.setContent(
            errorContainerId,
            EzString.hasText(currentEm)
                ? EzHtml.build`
                    <div>
                        ${currentEm}
                    </div>
                    <div>
                        ${em}
                    </div>`
                : EzHtml.build`
                    <div>
                        ${em}
                    </div>`);

        EzUx.show(errorContainerId);

        if (EzUx.elementExists('EzSignInErrorResetPasswordLink')) {
            const resetLink = EzUx.findElement('EzSignInErrorResetPasswordLink');
            resetLink.onmousedown = (e) => {
                e.preventDefault();
                e.stopPropagation();
            };
            resetLink.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                EzSignInView.ezInstance.ezShowRequestPasswordReset();
                return false;
            };
        }

        if (EzSignInView.ezInstance.ezIds.containers.signInErrorTopId !== errorContainerId) {
            const currentEm = EzUx.getContent(EzSignInView.ezInstance.ezIds.containers.signInErrorTopId);

            EzUx.setContent(
                EzSignInView.ezInstance.ezIds.containers.signInErrorTopId,
                EzString.hasText(currentEm)
                    ? EzHtml.build`
                        <div>
                            ${currentEm}
                        </div>
                        <div>
                            ${em}
                        </div>`
                    : EzHtml.build`
                        <div>
                            ${em}
                        </div>`);
        }
    }

    /**
     * @public @method
     * Hides the error message box.
     */
    ezHideSignInErrorContainers() {
        const userNameErrorContainerId = `${EzSignInView.ezInstance.ezIds.inputs.userNameInputId}_ErrorContainer`;

        if (EzUx.elementExists(userNameErrorContainerId)) {
            EzUx.clearContent(userNameErrorContainerId);

            EzUx.hide(userNameErrorContainerId);
        }

        const passwordErrorContainerId = `${EzSignInView.ezInstance.ezIds.inputs.passwordInputId}_ErrorContainer`;

        if (EzUx.elementExists(passwordErrorContainerId)) {

            EzUx.clearContent(passwordErrorContainerId);

            EzUx.hide(passwordErrorContainerId);
        }
    }

    /**
     * @public @method
     * @param {object} event
     */
    ezHandleEnterKey(event) {
        if (EzNumber.THIRTEEN === event.keyCode || EzNumber.THIRTEEN === event.which) {
            EzSignInView.ezInstance.ezSubmit();
        } else if (EzNumber._27 === event.keyCode || EzNumber._27 === event.which) {
            EzSignInView.ezInstance.ezCancel();
        }
    }

    /**
     * @public @method
     * Handles the password show/hide button click to toggle showing the password
     */
    ezToggleShowHidePassword() {
        const passwordInputEditor = EzUx.findElement(EzSignInView.ezInstance.ezIds.inputs.passwordInputId);

        if (!EzObject.isValid(passwordInputEditor)) {
            // Could not find the element, ignore the call
            globalThis.ezApi.ezclocker.ezLogger.error('Unable to toggle show/hide password: Password input not available.');

            return;
        }

        const passwordVisible = passwordInputEditor.type === 'text';

        passwordInputEditor.type = passwordVisible
            ? 'password'
            : 'text';

        EzUx.setElementTitle(
            EzSignInView.ezInstance.ezIds.buttons.passwordShowButtonId,
            passwordVisible
                ? 'Show Password'
                : 'Hide Password');

        EzUx.hide(
            passwordVisible
                ? EzSignInView.ezInstance.ezIds.images.hidePasswordImg
                : EzSignInView.ezInstance.ezIds.images.showPasswordImg);

        EzUx.show(
            passwordVisible
                ? EzSignInView.ezInstance.ezIds.images.showPasswordImg
                : EzSignInView.ezInstance.ezIds.images.hidePasswordImg);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     *
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezSignInView';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     *
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzSignInView_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     *
     * @type {EzSignInView}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzSignInView.ezApiName]
        ? globalThis.ezApi.ezclocker[EzSignInView.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     *
     * @returns {EzSignInView}
     */
    static get ezInstance() {
        return EzSignInView.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     *
     * @param {EzSignInView} ezInstance
     */
    static set ezInstance(ezInstance) {
        if (null != EzSignInView.#ezInstance) {
            throw new Error('EzSignInView\'s singleton instance is already reigstered with EzApi.');
        }

        EzSignInView.#ezInstance = ezInstance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     *
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzSignInView.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     *
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzSignInView.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     *
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzSignInView.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     *
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzSignInView.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzCookies.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzElementEventName.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzLocalBrowserInfo.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzLayoutHelper.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzUrlHelper.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzBrowserInfo.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzAnimator.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzValidation.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzAnalyticsAndMetrics.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzRequestPasswordResetDialog.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     *
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzSignInView.ezInstance &&
            EzRegistrationState.REGISTERED === EzSignInView.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     *
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzSignInView.#ezCanRegister && !EzSignInView.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzSignInView, EzSignInView.ezApiName);
        }

        return EzSignInView.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzSignInView.ezApiName
     *     2) Property getter EzSignInView.ezEventNames
     *     3) Property getter EzSignInView.ezInstance
     *     4) Property setter EzSignInView.ezInstance
     *     5) Property getter EzSignInView.ezApiRegistrationState
     *     6) Property setter EzSignInView.ezApiRegistrationState
     *     7) Property getter EzSignInView.#ezCanRegister
     *     8) Property getter EzSignInView.#ezIsRegistered
     *     9) Method EzSignInView.#ezRegistrator()
     */
    static {
        if (!EzSignInView.#ezIsRegistered) {
            EzSignInView.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzSignInView.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzSignInView.ezOnEzApiReadyEventName,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzCookies.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzElementEventName.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzLocalBrowserInfo.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzLayoutHelper.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzUrlHelper.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzNavigation.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzBrowserInfo.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzAnimator.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzValidation.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzAnalyticsAndMetrics.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzRequestPasswordResetDialog.ezEventNames.onReady,
                    EzSignInView.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
